"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api";

type Control = {
  id: string;
  framework: string[];
  refs: string[];
  title: string;
  description: string;
  status: "implemented" | "partial" | "planned" | "n/a";
  evidence?: string[];
};

type Compliance = {
  framework: string;
  frameworks?: string[];
  disclaimer: string;
  domain: string;
  score: number;
  byFramework?: Record<string, number>;
  counts: Record<string, number>;
  audit?: { total: number; last24h: number; failures24h: number };
  zeroSpend?: boolean;
  hipaa?: { status: string; note: string; policyUrl: string; docs: string };
  updatedAt?: string;
  controls: Control[];
};

/** Shown if API is offline — public page never hard-crashes */
const FALLBACK: Compliance = {
  framework:
    "SOC 2 readiness · ISO/IEC 27001 mapping · HIPAA Security & Privacy Rule readiness",
  frameworks: ["SOC2", "ISO27001", "HIPAA"],
  disclaimer:
    "Virtuous implements security and privacy controls for readiness (similar to GRC tools like Vanta). This is not a formal certificate or BAA.",
  domain: "virtuous.work",
  score: 0,
  byFramework: { SOC2: 0, ISO27001: 0, HIPAA: 0 },
  counts: { implemented: 0, partial: 0, planned: 0, "n/a": 0 },
  controls: [
    {
      id: "CRYPTO-01",
      framework: ["SOC2", "ISO27001", "HIPAA"],
      refs: ["TLS"],
      title: "Encryption in transit",
      description:
        "Public traffic uses HTTPS via Cloudflare when virtuous.work is connected.",
      status: "implemented",
    },
    {
      id: "PRIV-01",
      framework: ["SOC2", "ISO27001", "HIPAA"],
      refs: ["Privacy"],
      title: "Privacy notice & terms",
      description: "Published Privacy Policy and Terms of Use.",
      status: "implemented",
    },
    {
      id: "AI-01",
      framework: ["SOC2", "ISO27001", "HIPAA"],
      refs: ["Min. necessary"],
      title: "AI safety & data minimization",
      description:
        "Local-first AI by default; do not put full PHI or secrets in chat.",
      status: "implemented",
    },
  ],
  hipaa: {
    status: "readiness",
    note: "Live control scores load when the Virtuous API is online on the host PC.",
    policyUrl: "/privacy",
    docs: "docs/security/HIPAA.md",
  },
};

const statusLabel: Record<string, string> = {
  implemented: "Implemented",
  partial: "Partial",
  planned: "Planned",
  "n/a": "N/A",
};

const statusClass: Record<string, string> = {
  implemented: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  partial: "bg-amber-500/15 text-amber-800 dark:text-amber-200",
  planned: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
  "n/a": "bg-slate-500/10 text-slate-500",
};

const FILTERS = ["ALL", "SOC2", "ISO27001", "HIPAA"] as const;

export default function TrustPage() {
  const [data, setData] = useState<Compliance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("ALL");
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<Compliance>("/api/security/compliance", { timeoutMs: 10_000 })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setUsingFallback(false);
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setData(FALLBACK);
        setUsingFallback(true);
        setError(
          e instanceof Error
            ? e.message
            : "Live scores unavailable — showing summary.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    if (!data?.controls) return [];
    if (filter === "ALL") return data.controls;
    return data.controls.filter((c) => c.framework.includes(filter));
  }, [data, filter]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <p className="text-xs uppercase tracking-wider text-[var(--faint)]">
        Trust Center
      </p>
      <h1 className="mt-1 text-3xl font-semibold text-title">
        Security &amp; compliance
      </h1>
      <p className="mt-2 text-sm text-body">
        Domain:{" "}
        <strong className="text-title">
          {data?.domain ?? "virtuous.work"}
        </strong>
        . Controls modeled after GRC programs (e.g.{" "}
        <strong>Vanta</strong>) for <strong>SOC 2</strong>,{" "}
        <strong>ISO/IEC 27001</strong>, and <strong>HIPAA</strong> readiness.
      </p>

      <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-body">
        <strong className="text-title">Not a certificate or BAA.</strong> This
        page is a live control map. Official SOC 2, ISO 27001, or HIPAA
        attestation requires legal analysis, BAAs where needed, risk analysis,
        and often an independent auditor.
      </div>

      {usingFallback && error ? (
        <p className="mt-4 text-sm text-[var(--muted)]">
          Live API scores unavailable ({error}). Showing a short public summary.
          Full scores appear when the host PC API is online.
        </p>
      ) : null}

      {data && (
        <>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
              <div className="text-xs text-[var(--faint)]">Overall readiness</div>
              <div className="mt-1 text-3xl font-semibold text-title">
                {data.score > 0 ? `${data.score}%` : "—"}
              </div>
            </div>
            {(["SOC2", "ISO27001", "HIPAA"] as const).map((fw) => (
              <div
                key={fw}
                className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4"
              >
                <div className="text-xs text-[var(--faint)]">{fw}</div>
                <div className="mt-1 text-2xl font-semibold text-title">
                  {data.byFramework?.[fw]
                    ? `${data.byFramework[fw]}%`
                    : "—"}
                </div>
              </div>
            ))}
          </div>

          {data.counts && data.score > 0 ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
                <div className="text-xs text-[var(--faint)]">Controls</div>
                <div className="mt-1 text-sm text-body">
                  <span className="font-semibold text-title">
                    {data.counts.implemented ?? 0}
                  </span>{" "}
                  implemented ·{" "}
                  <span className="font-semibold text-title">
                    {data.counts.partial ?? 0}
                  </span>{" "}
                  partial
                </div>
              </div>
              {data.audit ? (
                <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
                  <div className="text-xs text-[var(--faint)]">
                    Audit log (24h)
                  </div>
                  <div className="mt-1 text-sm text-body">
                    {data.audit.last24h} events · {data.audit.failures24h}{" "}
                    fail/denied
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {data.hipaa && (
            <div className="mt-4 rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-sm text-body">
              <strong className="text-title">HIPAA mode:</strong>{" "}
              {data.hipaa.status} — {data.hipaa.note}{" "}
              <Link
                href={data.hipaa.policyUrl || "/privacy"}
                className="text-[var(--accent)] underline"
              >
                Privacy
              </Link>
            </div>
          )}

          {data.updatedAt ? (
            <p className="mt-4 text-xs text-[var(--faint)]">
              Updated {new Date(data.updatedAt).toLocaleString()}
              {data.zeroSpend != null
                ? ` · Zero-spend AI: ${data.zeroSpend ? "on" : "off"}`
                : ""}
            </p>
          ) : null}

          <div className="mt-8 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  filter === f
                    ? "bg-[var(--accent)] text-white"
                    : "bg-[var(--card)] text-body border border-[var(--border)]"
                }`}
              >
                {f === "ALL" ? "All frameworks" : f}
              </button>
            ))}
          </div>

          <h2 className="mt-6 text-lg font-semibold text-title">
            Control catalog
            <span className="ml-2 text-sm font-normal text-[var(--faint)]">
              ({visible.length})
            </span>
          </h2>
          <ul className="mt-4 space-y-3">
            {visible.map((c) => (
              <li
                key={c.id}
                className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-[var(--faint)]">
                    {c.id}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass[c.status]}`}
                  >
                    {statusLabel[c.status]}
                  </span>
                  {c.framework.map((fw) => (
                    <span
                      key={fw}
                      className="rounded-full bg-slate-500/10 px-2 py-0.5 text-[10px] font-medium text-[var(--muted)]"
                    >
                      {fw}
                    </span>
                  ))}
                </div>
                <p className="mt-1 text-[10px] text-[var(--faint)]">
                  {c.refs.join(" · ")}
                </p>
                <h3 className="mt-2 font-semibold text-title">{c.title}</h3>
                <p className="mt-1 text-sm text-body">{c.description}</p>
              </li>
            ))}
          </ul>
        </>
      )}

      <section className="mt-12 space-y-2 text-sm text-body">
        <h2 className="text-lg font-semibold text-title">Also see</h2>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <Link href="/privacy" className="text-[var(--accent)] underline">
              Privacy Policy
            </Link>{" "}
            (includes health data / PHI notes)
          </li>
          <li>
            <Link href="/terms" className="text-[var(--accent)] underline">
              Terms of Use
            </Link>
          </li>
          <li className="text-[var(--muted)]">
            Credentials vault, install app, and Earn are available after
            sign-in (Earn is Tier 1 only).
          </li>
        </ul>
      </section>
    </div>
  );
}
